import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type PagedResult } from "../../lib/api";
import { humanise } from "../../lib/format";
import { useSession } from "../../app/session";
import { useEventContext } from "../../app/eventContext";
import { Icon } from "../ui/Icon";
import { Button, toastError } from "../ui/primitives";
import { P } from "../../lib/permissions";

interface Insight {
  id: string;
  capability: string;
  title: string;
  summary: string;
  severity: string;
  confidencePercent: number;
  moduleKey: string | null;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  suggestedAction: string | null;
  isDeterministic: boolean;
  isAcknowledged: boolean;
  isResolved: boolean;
  workItemId: string | null;
  eventId: string | null;
  generatedAtUtc: string;
}

interface AiAnswer {
  conversationId: string;
  answer: string;
  usedSources: string[];
  providerKey: string;
  model: string;
}

/** Matches AiAttentionItemDto: one signal behind the greeting, and where it is dealt with. */
interface AttentionItem {
  key: string;
  label: string;
  count: number;
  severity: string;
  link: string;
}

/**
 * Matches AiAttentionDto. The greeting is counted on the server from open incidents,
 * critical work, event readiness and high-severity findings, so it cannot say that
 * nothing is blocking beside a critical record.
 */
interface Attention {
  eventId: string | null;
  headline: string;
  items: AttentionItem[];
  band: string | null;
  healthScore: number | null;
  coversAllSignals: boolean;
}

const SEVERITY_CLASS: Record<string, string> = {
  Critical: "",
  High: "",
  Medium: " railitem--amber",
  Low: " railitem--blue",
  Informational: " railitem--green",
};

/**
 * The assistant rail from the approved screens.
 *
 * Everything here is real: the blockers are the deterministic analysers' current
 * findings for this event, and the answer box calls the AI gateway, which can
 * only read records the signed-in user is permitted to see. Nothing is
 * pre-written copy.
 */
export function AiRail() {
  const { user, can } = useSession();
  const { currentId } = useEventContext();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<AiAnswer | null>(null);

  const canUseAi = can(P.AiView);

  const { data: insights } = useQuery({
    queryKey: ["insights", "rail", currentId],
    enabled: canUseAi,
    queryFn: () =>
      api.get<PagedResult<Insight>>("/ai/insights", {
        eventId: currentId ?? undefined,
        unresolvedOnly: true,
        page: 1,
        pageSize: 6,
      }),
  });

  const { data: attention } = useQuery({
    // Under the insights key, so re-analysing an event refreshes the greeting with its findings.
    queryKey: ["insights", "rail", "attention", currentId],
    enabled: canUseAi,
    queryFn: () => api.get<Attention>("/ai/attention", { eventId: currentId ?? undefined }),
  });

  const ask = useMutation({
    mutationFn: (text: string) =>
      api.post<AiAnswer>("/ai/ask", {
        question: text,
        eventId: currentId,
        contextEntityType: null,
        contextEntityId: null,
        conversationId: answer?.conversationId ?? null,
      }),
    onSuccess: setAnswer,
    onError: (error) => toastError(error, "The assistant could not answer"),
  });

  if (!canUseAi) return null;

  const blockers = (insights?.items ?? []).filter(
    (insight) => insight.severity === "Critical" || insight.severity === "High",
  );

  const suggestions = (insights?.items ?? []).filter(
    (insight) => insight.suggestedAction && !blockers.includes(insight),
  );

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = user?.displayName.split(" ")[0] ?? "there";

  // Findings are listed one by one under Top blockers; the other signals link to where they are handled.
  const attentionLinks = (attention?.items ?? []).filter(
    (item) => item.key !== "critical-findings" && item.key !== "high-findings",
  );

  return (
    <aside className="rail" aria-label="Assistant">
      <div className="railgreet">
        <div className="row" style={{ marginBottom: 8 }}>
          <Icon.sparkle
            width={17}
            height={17}
            style={{ color: "var(--red-bright)" }}
          />
          <b style={{ flex: 1 }}>Wolfray AI</b>
          <span
            className="pill pill--green pill--round"
            style={{ font: "600 10px/1.4 var(--font)" }}
          >
            Online
          </span>
        </div>
        <b>
          {greeting}, {firstName}.
        </b>
        <p>
          {!attention
            ? "Checking what needs you…"
            : attention.items.length === 0 && attention.coversAllSignals
              ? `${attention.headline} Ask me anything about ${currentId ? "this event" : "your events"}.`
              : attention.headline}
        </p>

        <div className="railask">
          <input
            className="input"
            placeholder="Ask Wolfray AI anything…"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && question.trim()) {
                ask.mutate(question.trim());
              }
            }}
            aria-label="Ask Wolfray AI"
          />
          <Button
            type="button"
            tone="primary"
            size="sm"
            busy={ask.isPending}
            disabled={!question.trim()}
            onClick={() => ask.mutate(question.trim())}
            aria-label="Send"
            title="Send"
          >
            <Icon.arrowRight />
            {/* Named by its content as well as by aria-label, so every accessibility tree reads "Send". */}
            <span className="sr-only">Send</span>
          </Button>
        </div>

        <div className="railchips">
          {[
            "Summarise my tasks",
            "Show overdue items",
            "What needs approval?",
            "Draft a status update",
          ].map((chip) => (
            <button
              key={chip}
              className="railchip"
              onClick={() => {
                setQuestion(chip);
                ask.mutate(chip);
              }}
            >
              {chip}
            </button>
          ))}
        </div>

        {answer && (
          <div
            style={{
              marginTop: 12,
              padding: 11,
              background: "var(--ink-800)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r)",
            }}
          >
            <p style={{ font: "var(--t-small)", whiteSpace: "pre-wrap" }}>{answer.answer}</p>
            {answer.usedSources.length > 0 && (
              <p className="muted" style={{ font: "400 11px/1.5 var(--font)", marginTop: 7 }}>
                Based on: {answer.usedSources.join(", ")}
              </p>
            )}
          </div>
        )}
      </div>

      {attentionLinks.length > 0 && (
        <section className="rail__panel">
          <header className="rail__head">
            <Icon.bell />
            <h3>Needs attention</h3>
            <span className="rail__count">{attentionLinks.length}</span>
          </header>
          {attentionLinks.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`railitem${SEVERITY_CLASS[item.severity] ?? ""}`}
              onClick={() => navigate(item.link)}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{item.label.charAt(0).toUpperCase() + item.label.slice(1)}</b>
                <span>
                  {item.link.startsWith("/onsite")
                    ? "Onsite operations"
                    : item.link.startsWith("/work")
                      ? "Work"
                      : "Events"}
                </span>
              </span>
            </button>
          ))}
        </section>
      )}

      {blockers.length > 0 && (
        <section className="rail__panel">
          <header className="rail__head">
            <Icon.alert />
            <h3>Top blockers</h3>
            <span className="rail__count">{blockers.length}</span>
          </header>
          {blockers.map((insight) => (
            <button
              key={insight.id}
              className={`railitem${SEVERITY_CLASS[insight.severity] ?? ""}`}
              onClick={() => navigate(`/ai?insight=${insight.id}`)}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{insight.title}</b>
                <span>{insight.summary}</span>
              </span>
            </button>
          ))}
        </section>
      )}

      {suggestions.length > 0 && (
        <section className="rail__panel">
          <header className="rail__head">
            <Icon.sparkle />
            <h3>Suggested next actions</h3>
            <span className="rail__count">{suggestions.length}</span>
          </header>
          {suggestions.map((insight) => (
            <button
              key={insight.id}
              className="railitem railitem--blue"
              onClick={() => navigate(`/ai?insight=${insight.id}`)}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{insight.suggestedAction}</b>
                <span>
                  {humanise(insight.moduleKey ?? insight.capability)} ·{" "}
                  {insight.isDeterministic ? "Rule-based" : "AI"}
                </span>
              </span>
            </button>
          ))}
          <Button
            tone="primary"
            block
            style={{ marginTop: 8 }}
            onClick={() => navigate("/ai")}
          >
            View all suggestions
            <Icon.arrowRight />
          </Button>
        </section>
      )}
    </aside>
  );
}
